import CryptoJS from "crypto-js";
import { ICustomerAddress } from "../models/ICustomerAddress";
import { ITokenData } from "../services/ITokenData";

const key = "Mtoken";

export const decrypt = (cipherText: string) => {
  const bytes = CryptoJS.AES.decrypt(cipherText, key);
  const plainText = bytes.toString(CryptoJS.enc.Utf8);
  return plainText;
};

export const encrypt = (plainText: string) => {
  return CryptoJS.AES.encrypt(plainText, key).toString();
};

export const getWebToken = () => {
  const stToken = localStorage.getItem("MA");
  if (stToken) {
    try {
      const plainText = decrypt(stToken);
      const objToken: ITokenData = JSON.parse(plainText);
      return objToken;
    } catch (error) {
      localStorage.removeItem("MA");
    }
  }
  return null;
};

export const saveUserToken = (data: ITokenData) => {
  logAuthEvent("token-saved", {
    userId: data.userId,
    hasRefreshToken: !!data.refreshToken,
    refreshTail: data.refreshToken?.slice(-6),
    expiration: data.expiration,
  });
  localStorage.setItem("userSK", data.secretKey);
  localStorage.setItem(
    "user",
    CryptoJS.AES.encrypt(JSON.stringify(data), data.secretKey).toString()
  );
  window.dispatchEvent(new CustomEvent("user-logged-in"));
};

export const getUserToken = () => {
  const sk = localStorage.getItem("userSK");
  const stToken = localStorage.getItem("user");
  if (sk && stToken) {
    try {
      const bytes = CryptoJS.AES.decrypt(stToken, sk);
      const plainText = bytes.toString(CryptoJS.enc.Utf8);
      const objToken: ITokenData = JSON.parse(plainText);
      return objToken;
    } catch (error) {
      logAuthEvent("token-decrypt-failed", { error: String(error) });
      localStorage.removeItem("user");
      localStorage.removeItem("userSK");
    }
  }
  return null;
};

/* Oturum teşhis kaydı — beklenmedik logout'un sebebini bulmak için.
   clearUserStorage bu anahtarı silmez; konsolda görmek için:
   JSON.parse(localStorage.authDebugLog) */
const AUTH_LOG_KEY = "authDebugLog";
const AUTH_LOG_MAX = 40;

export const logAuthEvent = (event: string, details: Record<string, unknown> = {}) => {
  try {
    const entry = { at: new Date().toISOString(), page: window.location.pathname, event, ...details };
    const log = JSON.parse(localStorage.getItem(AUTH_LOG_KEY) ?? "[]") as unknown[];
    log.push(entry);
    localStorage.setItem(AUTH_LOG_KEY, JSON.stringify(log.slice(-AUTH_LOG_MAX)));
    console.warn("[auth]", entry);
  } catch {
    // teşhis kaydı uygulamayı asla bozmamalı
  }
};

export const isLoggedIn = (): boolean => getUserToken() !== null;

export const clearUserStorage = () => {
  localStorage.removeItem("user");
  localStorage.removeItem("userSK");
  localStorage.removeItem("addresses");
  localStorage.removeItem("defaultAddress");
};

export const saveAddresses = (addresses: ICustomerAddress[]) => {
  localStorage.setItem("addresses", JSON.stringify(addresses));
  const defaultAddr = addresses.find((a) => a.customerAddressIsDefault) ?? null;
  if (defaultAddr) {
    localStorage.setItem("defaultAddress", JSON.stringify(defaultAddr));
  } else {
    localStorage.removeItem("defaultAddress");
  }
};

export const getAddresses = (): ICustomerAddress[] | null => {
  const stored = localStorage.getItem("addresses");
  if (stored) {
    try {
      return JSON.parse(stored) as ICustomerAddress[];
    } catch {
      localStorage.removeItem("addresses");
    }
  }
  return null;
};

export const getDefaultAddress = (): ICustomerAddress | null => {
  const stored = localStorage.getItem("defaultAddress");
  if (stored) {
    try {
      return JSON.parse(stored) as ICustomerAddress;
    } catch {
      localStorage.removeItem("defaultAddress");
    }
  }
  return null;
};

const DEVICE_UID_KEY = "deviceUid";

export const logout = () => {
  // Cihaz kimliği oturumdan bağımsız — silinirse her çıkış/girişte backend'de yeni token satırı açılır
  const deviceUid = localStorage.getItem(DEVICE_UID_KEY);
  const authLog = localStorage.getItem(AUTH_LOG_KEY);
  localStorage.clear();
  if (deviceUid) localStorage.setItem(DEVICE_UID_KEY, deviceUid);
  if (authLog) localStorage.setItem(AUTH_LOG_KEY, authLog);
  logAuthEvent("manual-logout");
};

export const saveRememberMe = (email: string, password: string) => {
  localStorage.setItem("rememberMe", encrypt(JSON.stringify({ email, password })));
};

export const getRememberMe = (): { email: string; password: string } | null => {
  const stored = localStorage.getItem("rememberMe");
  if (!stored) return null;
  try {
    return JSON.parse(decrypt(stored));
  } catch {
    localStorage.removeItem("rememberMe");
    return null;
  }
};

const getBrowserName = (): string => {
  const ua = navigator.userAgent;
  if (ua.includes("Edg/")) return "Edge";
  if (ua.includes("Chrome")) return "Chrome";
  if (ua.includes("Firefox")) return "Firefox";
  if (ua.includes("Safari")) return "Safari";
  if (ua.includes("OPR") || ua.includes("Opera")) return "Opera";
  return "Bulunamadi";
};

const getDeviceUid = (): string => {
  let uid = localStorage.getItem(DEVICE_UID_KEY);
  if (!uid) {
    uid = typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
    localStorage.setItem(DEVICE_UID_KEY, uid);
  }
  return uid;
};

// Backend erişim token'ını (kullanıcı, deviceId) başına TEK satırda tutar. Sadece tarayıcı
// adı gönderilince aynı hesabın tüm Chrome oturumları (telefon, başka PC, gizli pencere)
// aynı satırı paylaşıyor ve birinin girişi diğerinin token'ını geçersiz kılıyordu.
export const getDeviceId = (): string => `${getBrowserName()}-${getDeviceUid()}`;
